import Config from 'volta/config/environment';

import { action } from '@ember/object';
import ApplicationRoute from '../application/route';
import { inject as service } from '@ember/service';

import type RouterService from '@ember/routing/router-service';
// @ts-ignore
import type CookiesService from 'ember-cookies/services/cookies';
import type IntlService from 'ember-intl/services/intl';
import type SessionService from 'ember-simple-auth/services/session';

import type AppSettings from 'volta/services/app-settings';
import type BvFlashService from 'volta/services/bv-flash';
import type StoreService from 'volta/services/store';

type Transition = ReturnType<RouterService['transitionTo']>;

const SESSION_COOKIE_NAME: string = Config.auth.sessionCookieName;

export default class LoginRoute extends ApplicationRoute {
  @service cookies!: CookiesService;
  @service store!: StoreService;
  @service bvFlash!: BvFlashService;
  @service intl!: IntlService;
  @service appSettings!: AppSettings;
  @service session!: SessionService;

  queryParams = {
    state: { refreshModel: false }
  };

  async beforeModel(transition: Transition) {
    await super.beforeModel(transition);
    this.session.prohibitAuthentication('protected.dashboard');
  }

  async model() {
    const res = await this.appSettings.fetchBaseTenant.perform();
    if (!this.cookies.exists(SESSION_COOKIE_NAME)) {
      await delay(1400);
    }
    return res;
  }

  // Allow error and loading template to be shown
  @action
  error() {
    return true;
  }

  @action
  loading() {
    return true;
  }

  afterModel(model: {}, transition: Transition) {
    super.afterModel(model, transition);
    const queryParams = transition.to?.queryParams;
    return this.checkSession(queryParams);
  }

  // This route can be redirected to from the backend after a external provider (like Okta) authentication
  // The request should carry a cookie with the stringified JWT session
  // If it exists we just need to parse it and authorize the session
  // with ember-simple-auth like we would do normally
  async checkSession(queryParams?: { state?: string }) {
    // If Okta user is not linked to a Volta session, throws a error
    if (queryParams?.state) {
      this.bvFlash.error(this.intl.t(atob(queryParams.state)));
      this.replaceWith('login', { queryParams: { state: undefined } });
      return;
    }
    try {
      if (this.cookies.exists(SESSION_COOKIE_NAME)) {
        const sessionCookie = this.cookies.read(SESSION_COOKIE_NAME);
        const session = JSON.parse(sessionCookie);
        if (session) {
          this.cookies.clear(SESSION_COOKIE_NAME);
          await this.session.authenticate('authenticator:session', session);
        }
      }
    } catch (error) {
      this.session.store?.clear();
      throw error;
    }
  }
}

function delay(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
